import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import StakeholderInput from "./StakeholderInput";
import { createStakeholderTag } from "@/lib/api";

/**
 * AddStakeholderDialog Component
 * 
 * Modal dialog for adding a stakeholder to an order after creation.
 * Features:
 * - Uses shadcn/ui Dialog component
 * - Includes StakeholderInput for user selection
 * - Add role note text input (integrated in StakeholderInput)
 * - Submit handler calling createStakeholderTag API
 * - Loading state during submission
 * - Success/error toast notifications
 * - Validation error handling (duplicate, role note too long)
 * - Closes dialog on success
 * 
 * @param {Boolean} open - Whether the dialog is open
 * @param {Function} onOpenChange - Callback to control dialog open state
 * @param {String} orderId - The order ID to add stakeholder to
 * @param {Function} onSuccess - Callback when stakeholder is successfully added
 * 
 * @example
 * // Usage in OrderDetailPage or similar component:
 * import AddStakeholderDialog from '@/components/AddStakeholderDialog';
 * 
 * function OrderDetailPage() {
 *   const [addDialogOpen, setAddDialogOpen] = useState(false);
 *   
 *   const handleAddSuccess = async () => {
 *     // Refresh order data to show new stakeholder
 *     const res = await api.get(`/orders/${orderId}`);
 *     setOrder(res.data);
 *   };
 *   
 *   return (
 *     <>
 *       <InvolvedPeopleSection
 *         order={order}
 *         onAddStakeholder={() => setAddDialogOpen(true)}
 *         currentUser={currentUser}
 *       />
 *       <AddStakeholderDialog
 *         open={addDialogOpen}
 *         onOpenChange={setAddDialogOpen}
 *         orderId={order.id}
 *         onSuccess={handleAddSuccess}
 *       />
 *     </>
 *   );
 * }
 */
const AddStakeholderDialog = ({ open, onOpenChange, orderId, onSuccess }) => {
  const [selectedStakeholders, setSelectedStakeholders] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when dialog closes
  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      setSelectedStakeholders([]);
      setIsSubmitting(false);
    }
    onOpenChange(newOpen);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: at least one stakeholder must be selected
    if (selectedStakeholders.length === 0) {
      toast.error("Please select at least one person to add");
      return;
    }

    setIsSubmitting(true);

    try {
      // Create stakeholder tags for each selected user
      const promises = selectedStakeholders.map((stakeholder) =>
        createStakeholderTag(orderId, {
          user_id: stakeholder.user_id,
          role_note: stakeholder.role_note || "",
        })
      );

      await Promise.all(promises);

      // Show success message
      const message =
        selectedStakeholders.length === 1
          ? "Person added successfully"
          : `${selectedStakeholders.length} people added successfully`;
      toast.success(message);

      // Close dialog and notify parent
      handleOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {

      // Handle specific error codes
      const errorCode = error.response?.data?.error_code;
      const errorDetail = error.response?.data?.detail;

      if (errorCode === "DUPLICATE_STAKEHOLDER") {
        toast.error(errorDetail || "This person is already tagged on this order");
      } else if (errorCode === "ROLE_NOTE_TOO_LONG") {
        toast.error(errorDetail || "Role note must be 100 characters or less");
      } else if (errorCode === "USER_NOT_FOUND") {
        toast.error(errorDetail || "Selected user not found");
      } else if (errorCode === "ORDER_NOT_FOUND") {
        toast.error(errorDetail || "Order not found");
      } else if (errorCode === "INSUFFICIENT_PERMISSIONS") {
        toast.error(errorDetail || "You do not have permission to tag stakeholders");
      } else {
        toast.error("Failed to add person. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Person to Order</DialogTitle>
            <DialogDescription>
              Search for and select people who were involved in this order. You can add
              optional role notes to describe their contributions.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <StakeholderInput
              value={selectedStakeholders}
              onChange={setSelectedStakeholders}
              disabled={isSubmitting}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || selectedStakeholders.length === 0}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Person"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddStakeholderDialog;
